import type { QueryClient } from '@tanstack/react-query'

type KeyHead = {
  _id?: string
  query?: { organization?: number | string }
  path?: { id?: number }
}

const LIST_QUERY_IDS = [
  'applicationsApplicationsList',
  'applicationsApplicationsRepositoriesList'
]

const head = (queryKey: readonly unknown[]): KeyHead | undefined =>
  queryKey[0] as KeyHead | undefined

/**
 * All paginated list queries for this workspace's applications and
 * repositories.
 *
 * `refetchType: 'all'` because the app sets `refetchOnMount: false` globally
 * (see `ReactQueryProvider`): a list that is unmounted while this runs would
 * otherwise stay marked stale and still serve the old rows when it remounts.
 *
 * The organization filter is compared as a string — the repositories list sends
 * it as one while the applications list sends a number.
 */
export function invalidateWorkspaceApplicationsListQueries(
  queryClient: QueryClient,
  workspaceId: number
): Promise<void> {
  return queryClient.invalidateQueries({
    refetchType: 'all',
    predicate: (q) => {
      const k = head(q.queryKey)

      return (
        LIST_QUERY_IDS.includes(k?._id ?? '') &&
        String(k?.query?.organization) === String(workspaceId)
      )
    }
  })
}

export function invalidateWorkspaceApplicationDetailQueries(
  queryClient: QueryClient,
  applicationId: number
): Promise<void> {
  return queryClient.invalidateQueries({
    refetchType: 'all',
    predicate: (q) => {
      const k = head(q.queryKey)

      return (
        k?._id === 'applicationsApplicationsRetrieve' &&
        k.path?.id === applicationId
      )
    }
  })
}
